import { useEffect, useRef, useState, useMemo, useContext } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, TouchableOpacity, ScrollView, Pressable } from "react-native";
import { GreenCircle, RightArrow, LeftArrow, Assessment } from "../components/svgs/common/index";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import {
  useMeQuery,
  useGetNotEnrolledCoursesBySchoolQuery,
  useGetEnrolledCoursesBySchoolQuery,
  useGetCourseByIdQuery,
} from "../graphql/generated/graphql";
import Loader from "../components/common/status/Loader/Loader";
import TimeLeftSmall from "../components/svgs/common/TimeLeftSmall";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { MotiView } from "moti";
import { useAppConfig } from "../AppConfigProvider";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import CatalogeCourseCard from "../components/lms/CatalogeCourseCard/CatalogeCourseCard";
import RecommendedCourseCard from "../components/lms/RecommendedCourseCard/RecommendedCourseCard";
import { useNavigation } from "@react-navigation/native";
import { CourseDetailsContext } from "../contexts/CourseDetailsContext";

function HomeView() {
  const { data: meData } = useMeQuery({});
  const navigation = useNavigation();

  const { isMedium } = useResponsiveScreen();

  const userName = meData?.me?.name || "";
  const posthog = usePostHog();
  const appConfig = useAppConfig();
  const integrationId = appConfig.axioSchools[0];
  const scrollViewRef = useRef(null);

  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);

  const { onEnroll } = useContext(CourseDetailsContext);

  useEffect(() => {
    posthog?.capture("Course Catalogue Page Visited");
  }, []);

  const { data: courses } = useGetEnrolledCoursesBySchoolQuery({
    variables: {
      schoolId: integrationId,
      page: 1,
      pageSize: 10,
    },
    skip: !integrationId,
  });

  const myCourses = useMemo(() => {
    const parsedCourses =
      courses?.getEnrolledCoursesBySchool?.data?.courses?.map((course, index) => {
        let imageUrl = course.thumbnail || "https://images.unsplash.com/photo-1687360442216-6c01ac3322a3";

        return {
          id: course.id || String(index + 1),
          title: course.name || "Course Name",
          progress: course.progress || 0,
          imageUrl,
          lastModified: course.last_modified ? new Date(course.last_modified) : new Date(0),
          description: course.description || "",
          isCompleted: (course.progress || 0) === 100,
        };
      }) || [];

    const sortedCourses = parsedCourses
      .filter((course) => !course.isCompleted)
      .sort((a, b) => b.lastModified - a.lastModified);

    return sortedCourses;
  }, [courses?.getEnrolledCoursesBySchool?.data]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  useEffect(() => {
    if (myCourses.length > 0 && myCourses[0]?.id) {
      setSelectedCourseId(myCourses[0].id);
    }
  }, [myCourses]);

  const { data: courseDetails, loading: courseLoading } = useGetCourseByIdQuery({
    variables: { id: selectedCourseId },
    skip: !selectedCourseId,
  });

  const firstModuleName = courseDetails?.getCourseById?.data?.[0]?.modules?.[0]?.title || "Continue your course";

  const displayCourses = myCourses.length > 0 ? myCourses : [];

  const titles = [];

  const { data: recommendedCoursesData, loading: recommendedCoursesLoading } = useGetNotEnrolledCoursesBySchoolQuery({
    variables: {
      schoolId: integrationId,
      page: 1,
      pageSize: 3,
    },
    skip: !integrationId,
  });

  const recommendedCourses = useMemo(() => {
    return recommendedCoursesData?.getNotEnrolledCoursesBySchool?.data?.courses?.slice(0, 3) || [];
  }, [recommendedCoursesData]);

  const handlePress = (id) => {
    const course = myCourses?.filter((course) => course.id === id)[0];
    navigation.replace("Custom Course Student", {
      course_id: course.id,
      integration_id: integrationId,
    });
  };
  const scrollLeft = () => {
    if (scrollViewRef.current && currentScrollIndex > 0) {
      const newIndex = currentScrollIndex - 1;
      scrollViewRef.current.scrollTo({ x: newIndex * 280, animated: true });
      setCurrentScrollIndex(newIndex);
    }
  };

  const scrollRight = () => {
    if (scrollViewRef.current && currentScrollIndex < displayCourses.length - 1) {
      const newIndex = currentScrollIndex + 1;
      scrollViewRef.current.scrollTo({ x: newIndex * 280, animated: true });
      setCurrentScrollIndex(newIndex);
    }
  };

  const handleScroll = (event) => {
    const contentOffsetX = event.nativeEvent.contentOffset.x;
    const index = Math.round(contentOffsetX / 280);
    setCurrentScrollIndex(index);
  };

  const handleEnrollPress = (courseId, hasCost) => {
    onEnroll(courseId, hasCost, () => {
      navigation.navigate("Custom School Student", {
        integration_id: integrationId,
      });
    });
  };

  return (
    <NoChatBarLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}>
        <View style={{ flexDirection: "column", justifyContent: "flex-start" }}>
          <View
            style={{
              flexDirection: isMedium ? "row" : "column",
              justifyContent: isMedium ? "space-between" : "flex-start",
              alignItems: isMedium ? "center" : "flex-start",
              gap: 8,
            }}>
            <CustomText
              text={"Welcome back, " + userName}
              size={isMedium ? "m" : "xs"}
              useTranslationText={false}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={{ letterSpacing: -2, color: "#101828" }}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Here’s what’s happening in your learning journey today"
            size="m"
            weight="normal"
            style={{ color: "#667085" }}
          />
        </View>
      </MotiView>
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xs" />
      {myCourses.length > 0 && (
        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          delay={10}
          transition={{ type: "timing", duration: 500 }}>
          <View
            style={[
              styles.engagementContainer,
              { flexDirection: isMedium ? "row" : "column", alignItems: isMedium ? "center" : "flex-start", gap: 20 },
            ]}>
            <View style={{ flex: 1, flexDirection: "column", gap: 8 }}>
              <View
                style={{
                  paddingTop: 8,
                  flexDirection: isMedium ? "row" : "column",
                  alignItems: isMedium ? "center" : "flex-start",
                  gap: 8,
                  flexWrap: "nowrap",
                }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "nowrap",
                  }}>
                  <GreenCircle width={8} height={8} color="#30B83D" />
                  <CustomText
                    useTranslationText={false}
                    textType="text"
                    text="CONTINUE LEARNING"
                    size="s"
                    weight="bold"
                    style={{ color: "#30B83D", textTransform: "uppercase" }}
                  />
                </View>
                {isMedium && (
                  <View style={{ height: 12, borderLeftWidth: 1, borderLeftColor: "#D0D5DD", marginHorizontal: 4 }} />
                )}
                <CustomText
                  useTranslationText={false}
                  textType="text"
                  text={myCourses[0]?.title || "Latest Course"}
                  size="xs"
                  style={{ color: "#667085", letterSpacing: 1.26, textTransform: "uppercase" }}
                />
              </View>

              <CustomText
                useTranslationText={false}
                textType="text"
                text={courseLoading ? "Loading module..." : firstModuleName}
                size="xxl"
                weight="ultraBold"
                style={{ color: "#101828" }}
              />

              <View>
                <View
                  style={{
                    paddingTop: 8,
                    flexDirection: isMedium ? "row" : "column",
                    alignItems: isMedium ? "center" : "flex-start",
                    gap: 12,
                    width: "100%",
                  }}>
                  <View style={{ width: 200, height: 8, backgroundColor: "#E4E7EC", borderRadius: 4 }}>
                    <View
                      style={{
                        width: `${myCourses[0]?.progress || 0}%`,
                        height: 8,
                        backgroundColor: "#3E68FE",
                        borderRadius: 4,
                      }}
                    />
                  </View>

                  <CustomText
                    useTranslationText={false}
                    textType="text"
                    text={`${myCourses[0]?.progress || 0}% Complete`}
                    size="s"
                    style={{ color: "#667085" }}
                  />

                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 4,
                      flexWrap: "nowrap",
                    }}>
                    <TimeLeftSmall size={16} color="#667085" />
                    <CustomText
                      useTranslationText={false}
                      textType="text"
                      text="About 1 hour left"
                      size="s"
                      style={{ color: "#667085" }}
                    />
                  </View>
                </View>
              </View>
            </View>
            <CustomButton
              text="Resume Activity"
              size="s"
              style={{
                paddingTop: 8,
                paddingRight: 12,
                paddingBottom: 8,
                paddingLeft: 12,
              }}
              styleType="primary"
              onPress={() => {
                handlePress(myCourses[0]?.id);
              }}
            />
          </View>
        </MotiView>
      )}

      <CustomSpacing type="vertical" size="xl" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`My Courses (${displayCourses.length})`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
        />
        <View style={{ flexDirection: "row", alignItems: "center", gap: 9 }}>
          <TouchableOpacity
            style={{ padding: 9, backgroundColor: currentScrollIndex > 0 ? "#EAECF0" : "#F2F4F7", borderRadius: 25 }}
            onPress={scrollLeft}
            disabled={currentScrollIndex <= 0}>
            <LeftArrow color="#111111" />
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              padding: 9,
              backgroundColor: currentScrollIndex < displayCourses.length - 1 ? "#D0D5DD" : "#F2F4F7",
              borderRadius: 25,
            }}
            onPress={scrollRight}
            disabled={currentScrollIndex >= displayCourses.length - 1}>
            <RightArrow color="#111111" />
          </TouchableOpacity>
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        pagingEnabled={false}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        contentContainerStyle={{ gap: 20, paddingRight: 16 }}>
        {displayCourses.map((course) => (
          <View key={course.id}>
            <CatalogeCourseCard
              title={course.title}
              imageUrl={course.imageUrl}
              progress={course.progress}
              onPress={() => {
                handlePress(course.id);
              }}
            />
          </View>
        ))}
      </ScrollView>

      <CustomSpacing type="vertical" size="l" />
      <CustomSpacing type="vertical" size="l" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`Recent Grades`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
        />
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}></View>
      </View>

      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}>
        {titles.length === 0 ? (
          <View style={styles.engagementContainer}>
            <CustomText
              useTranslationText={false}
              text="No Grades Available"
              size="m"
              weight="bold"
              style={{ color: "#667085", textAlign: "center", width: "100%" }}
            />
          </View>
        ) : (
          <View
            style={{
              flex: "row",
              flexDirection: "row",
              gap: 7,
            }}>
            {titles.map((title, index) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  padding: 20,
                  gap: 12,
                  marginTop: 24,
                  flex: 1,
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: "#EAECF0",
                  backgroundColor: "#FFF",
                  overflow: "hidden",
                }}>
                <View style={{ flexDirection: "row", gap: 12 }}>
                  <Assessment />
                  <View style={{ flexDirection: "column", paddingRight: 20 }}>
                    <CustomText
                      useTranslationText={false}
                      text={title}
                      size="s"
                      weight="bold"
                      style={{ color: "#101828" }}
                    />
                    <CustomText
                      useTranslationText={false}
                      text={`Name of Course`}
                      size="xs"
                      style={{ color: "#667085" }}
                    />
                  </View>
                </View>
                <View style={{ alignSelf: "center", backgroundColor: "#3E68FE", padding: 5, borderRadius: 25 }}>
                  <CustomText
                    useTranslationText={false}
                    weight="ultraBold"
                    style={{ color: "white" }}
                    size="s"
                    text="32/100"
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </MotiView>

      <CustomSpacing type="vertical" size="l" />
      <CustomSpacing type="vertical" size="l" />

      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <CustomText
          useTranslationText={false}
          text={`Recommended Courses`}
          size="l"
          weight="bold"
          style={{ color: "#101828" }}
        />
        <Pressable
          onPress={() => navigation.navigate("Course Catalogue")}
          style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
          <CustomText useTranslationText={false} text="View All" size="m" style={{ color: "#667085" }} />
        </Pressable>
      </View>
      <CustomSpacing type="vertical" size="l" />
      {recommendedCoursesLoading ? (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <Loader />
        </View>
      ) : recommendedCourses.length === 0 ? (
        <View style={styles.engagementContainer}>
          <CustomText
            useTranslationText={false}
            text="No Recommended Courses Available"
            size="m"
            weight="bold"
            style={{ color: "#667085", textAlign: "center", width: "100%" }}
          />
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          pagingEnabled={false}
          scrollEventThrottle={16}
          contentContainerStyle={{
            flexDirection: "row",
            gap: 20,
            paddingRight: 16,
            marginBottom: 32,
          }}>
          {recommendedCourses.map((course) => (
            <RecommendedCourseCard
              key={course.id}
              title={course.name}
              onEnrollPress={() => handleEnrollPress(course?.id, !!course?.cost)}
              category={course.courseType || "Course"}
              price={course.cost || 0}
              description={course.description}
              imageUrl={course.thumbnail}
              tag={course.degreesHasCourse?.length > 0 ? "Degree Program" : "Public Course"}
            />
          ))}
        </ScrollView>
      )}
    </NoChatBarLayout>
  );
}

const styles = StyleSheet.create({
  engagementContainer: {
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    padding: 20,
    justifyContent: "space-between",
    marginTop: 24,
    marginBottom: 31,
    flex: 1,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFF",
    overflow: "hidden",
  },
});

export default HomeView;
